import { createSelector } from '@reduxjs/toolkit';
import { createBackgroundPaintable } from './preview/helpers/createBackgroundPaintable';
import { selectPrefabBackgroundAlpha, selectSceneHeight, selectSceneWidth } from './_redux-scene';
import { createPrefabPreviewPaintables } from './preview/helpers/createPrefabPreviewPaintables';
import { Paintable } from 'ff-canvas';
import { createOptionBindings, selectActiveVariant, selectActiveVariantId } from '@gift/website-state/slices/variantsSlice';
import { WebsiteState } from '@gift/website-state/store';
import { selectVariablePaintables } from '@gift/website-state/slices/variablesSlice';
import { Translate } from '@gift/types/generic/Translate';
import { PaintTarget } from '@gift/types/plugins/paintTargets';
import { BasicVariantOptions } from '@gift/basic-scene-core/BasicVariantOptions';
import { Prefab } from '@gift/basic-scene-core/Prefab';
import { Json } from '@gift/types/Json';
import { createUndoViewbox } from '@gift/common/helpers/transformParameters';
import { selectTransformParametersForPreviewByPrefabId } from '@gift/basic-scene-website/_redux-prefabs';
import { selectActivePrefabId } from '@gift/basic-scene-website/_redux-meta';

const { createOptionSelector } = createOptionBindings<BasicVariantOptions>();

const selectBackgroundAssetUrlById = createOptionSelector(o => o.backgroundAssetUrl);

export const selectBackgroundPaintableById = createSelector([
    selectSceneWidth,
    selectSceneHeight,
    (state: WebsiteState, variantId: string, prefabId: string) => selectUndoViewbox(state, prefabId),
    selectBackgroundAssetUrlById
], (width, height, undoViewbox, assetUrl) =>
    createBackgroundPaintable(width, height, undoViewbox, assetUrl!));

export const selectBackgroundPaintable = createSelector([
    (state: WebsiteState) => state,
    selectActiveVariantId,
    (state: WebsiteState, prefabId: string) => prefabId
], (state: WebsiteState, variantId, prefabId) => selectBackgroundPaintableById(state, variantId, prefabId));

export const selectPrefabPaintables = createSelector([
    (s: WebsiteState, prefab: Prefab) => prefab,
    (s: WebsiteState) => s.inputs,
    (s: WebsiteState) => selectActiveVariant(s),
    (s: WebsiteState, prefab: Prefab, t: Translate) => t
], createPrefabPreviewPaintables);

export const selectPrefabBackgroundPaintable = createSelector(
    (state: WebsiteState, prefabId: string) => selectBackgroundPaintable(state, prefabId),
    (state: WebsiteState) => selectPrefabBackgroundAlpha(state),
    (background, alpha) => ({ ...background, alpha }));

export const selectPreviewBackgroundPaintable = createSelector([
    state => state,
    selectActivePrefabId
], selectBackgroundPaintable);

export const selectPaintablesForPreview = createSelector([
    (state: WebsiteState, paintTarget: PaintTarget) => selectVariablePaintables(state, paintTarget),
    selectPreviewBackgroundPaintable,
], (paintables: Paintable[], background: Paintable) => paintables.concat(background));

export const selectPaintablesForPrefabPreview = createSelector(
    (state: WebsiteState, prefab: Prefab, variant: Json.Variant<any>, t: Translate) =>
        selectPrefabPaintables(state, prefab, variant, t),
    (state: WebsiteState, prefab: Prefab) => selectPrefabBackgroundPaintable(state, prefab.id),
    (paintables: Paintable[], background: Paintable) => paintables.concat(background));

export const selectUndoViewbox = createSelector([
    (state: WebsiteState, prefabId: string) => selectTransformParametersForPreviewByPrefabId(state, prefabId)
], createUndoViewbox);

const selectConfig3dById = createOptionSelector(o => o.config3d);

export const selectConfig3d = createSelector(
    [(state: WebsiteState) => state, selectActiveVariantId],
    (state: WebsiteState, id) => selectConfig3dById(state, id));